import React, { use, useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { CompetencyTypeDTO, MemberCompetencyDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { useCompetencyTypes } from '../../hooks/useCompetencyTypeApi';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface MemberCompetencyFormProps {
  initialValues: MemberCompetencyDTO;
  onSubmit: (data: MemberCompetencyDTO, competencyTypeIds?: string[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const MemberCompetencyForm: React.FC<MemberCompetencyFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const isCreateMode = !initialValues?.id;
  const [formData, setFormData] = useState<MemberCompetencyDTO>(initialValues);
  const [competencyTypeIds, setCompetencyTypeIds] = useState<string[]>(
    initialValues.competencyTypeId ? [initialValues.competencyTypeId] : []
  );
  const { uploadFile, getFileUrl } = useFileApi();

  // Use TanStack Query hook for competency types (automatically cached)
  const { data: competencyTypesData = [], isLoading: isLoadingCompetencyTypes } = useCompetencyTypes();
  const competencyTypes = competencyTypesData as CompetencyTypeDTO[];
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const validationRules: ValidationRules = {
    ...(isCreateMode
      ? {
          competencyTypeIds: [
            {
              custom: (value: string[]) => Array.isArray(value) && value.length > 0,
              message: 'At least one Competency Type is required',
            },
          ],
        }
      : {
          competencyTypeId: [
            { required: true, message: 'Competency Type is required' },
          ],
        }),
    certificateNumber: [
      { required: true, message: 'Certificate Number is required' },
    ],
    competencyCertificateId: [
      { required: true, message: 'Competency Certificate is required' },
    ],
    competencyIssueDate: [
      { required: true, message: 'Competency Issue Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Competency Issue Date must be a valid date',
      },
    ],
  };

  const { errors, validate } = useFormValidation(validationRules);

  const handleSave = () => {
    if (isCreateMode) {
      if (validate({ ...formData, competencyTypeIds })) {
        onSubmit(formData, competencyTypeIds);
      }
    } else if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, competencyCertificateId: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'competencies'
        );
        if (response && response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            competencyCertificateId: response.data
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, competencyCertificateId: false }));
      }
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        {isCreateMode ? (
          <FormField
            id="competencyTypeIds"
            label="Competency Type"
            type="multiselect"
            value={competencyTypeIds}
            onChange={(e) => setCompetencyTypeIds(e.target.value as unknown as string[])}
            placeholder="Select Competency Type(s)"
            required={true}
            error={errors.competencyTypeIds}
            options={competencyTypes.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingCompetencyTypes}
          />
        ) : (
          <FormField
            id="competencyTypeId"
            label="Competency Type"
            type="dropdown"
            value={formData.competencyTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Competency Type"
            required={true}
            error={errors.competencyTypeId}
            options={competencyTypes.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingCompetencyTypes}
          />
        )}
        <FormField
          id="certificateNumber"
          label="Certificate Number"
          type="text"
          value={formData.certificateNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Certificate Number"
          required={true}
          error={errors.certificateNumber}
        />
        {formData.competencyCertificateId && (
          <ImageLink
            fileId={formData.competencyCertificateId}
            label="Competency Certificate"
          />
        )}
        <FileUpload
          id="competencyCertificateUpload"
          onChange={handleFileChange}
          title="Upload Competency Certificate"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={true}
          error={errors.competencyCertificateId}
          isUploading={uploadingFiles['competencyCertificateId']}
        />
        <FormField
          id="competencyIssueDate"
          label="Competency Issue Date"
          type="date"
          value={formData.competencyIssueDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Competency Issue Date"
          required={true}
          error={errors.competencyIssueDate}
        />
        <FormField
          id="competencyExpiryDate"
          label="Competency Expiry Date"
          type="date"
          value={formData.competencyExpiryDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Competency Expiry Date"
          required={false}
          error={errors.competencyExpiryDate}
        />
      </FormSection>
    </Form>
  );
};

export default MemberCompetencyForm;
